import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminAuth, setAdminAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Store, Users, LogOut, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Small segmented toggle: New owner / Existing owner / Add later
function OwnerMode({ mode, setMode, allowSkip }) {
  const opts = [
    { k: "new", label: "New owner" },
    { k: "existing", label: "Existing owner" },
    ...(allowSkip ? [{ k: "skip", label: "Add later" }] : []),
  ];
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold">
      {opts.map((o) => (
        <button
          key={o.k}
          type="button"
          data-testid={`owner-mode-${o.k}`}
          onClick={() => setMode(o.k)}
          className={`px-3 py-1.5 rounded-md transition-colors ${mode === o.k ? "bg-white shadow-sm text-[var(--qd-charcoal)]" : "text-gray-500"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// The owner-picker body shared by both dialogs.
function OwnerFields({ mode, setMode, oForm, setOForm, pickEmail, setPickEmail, owners, allowSkip, testPrefix }) {
  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><Users className="w-3 h-3" aria-hidden /> Owner</div>
        <OwnerMode mode={mode} setMode={setMode} allowSkip={allowSkip} />
      </div>

      {mode === "new" && (
        <div className="space-y-2">
          <Input data-testid={`${testPrefix}-o-name`} placeholder="Owner name" value={oForm.name} onChange={(e) => setOForm({ ...oForm, name: e.target.value })} />
          <Input data-testid={`${testPrefix}-o-email`} type="email" placeholder="owner@example.com" value={oForm.email} onChange={(e) => setOForm({ ...oForm, email: e.target.value })} />
          <PasswordInput data-testid={`${testPrefix}-o-password`} placeholder="Password (leave blank to email an invite)" value={oForm.password} onChange={(e) => setOForm({ ...oForm, password: e.target.value })} />
          <div className="text-[11px] text-gray-400">Blank = they get a one-time link (valid 24h) to set their own password.</div>
        </div>
      )}

      {mode === "existing" && (
        owners.length ? (
          <select
            data-testid={`${testPrefix}-o-existing`}
            value={pickEmail}
            onChange={(e) => setPickEmail(e.target.value)}
            className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
          >
            <option value="">Select an existing owner…</option>
            {owners.map((o) => (
              <option key={o.id} value={o.email}>{o.name} · {o.email} ({(o.restaurant_ids || []).length} restaurant{(o.restaurant_ids || []).length === 1 ? "" : "s"})</option>
            ))}
          </select>
        ) : (
          <div className="text-xs text-gray-400">No existing owners yet — create one with "New owner".</div>
        )
      )}

      {mode === "skip" && (
        <div className="text-xs text-gray-400">This restaurant will have no owner. You can add one later from its card.</div>
      )}
    </div>
  );
}

export default function SuperConsole() {
  const auth = useAdminAuth();
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [owners, setOwners] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [ownerFor, setOwnerFor] = useState(null);
  const [rForm, setRForm] = useState({ name: "", currency: "INR" });
  const [oForm, setOForm] = useState({ name: "", email: "", password: "" });
  const [mode, setMode] = useState("new");       // create-dialog owner mode
  const [pickEmail, setPickEmail] = useState(""); // selected existing owner email
  const [busy, setBusy] = useState(false);
  const [delFor, setDelFor] = useState(null);     // restaurant pending deletion
  const [delName, setDelName] = useState("");     // type-to-confirm input

  useEffect(() => {
    if (!auth) { nav("/admin/login"); return; }
    if (auth.user?.role !== "super_admin") nav("/admin/dashboard");
  }, [auth, nav]);

  const load = () => api.get("/super/restaurants").then((r) => setRestaurants(r.data)).catch(() => {});
  const loadOwners = () => api.get("/super/owners").then((r) => setOwners(r.data)).catch(() => {});
  useEffect(() => { if (auth?.user?.role === "super_admin") { load(); loadOwners(); } }, [auth]);

  if (!auth || auth.user?.role !== "super_admin") return null;

  // Attach an owner to a restaurant based on the chosen mode. Returns a result
  // string for toasts, or null if nothing to do. Throws on API error.
  const attachOwner = async (rid, m, form, existingEmail) => {
    if (m === "skip") return null;
    if (m === "existing") {
      if (!existingEmail) return null;
      const r = await api.post(`/super/restaurants/${rid}/owner`, { email: existingEmail });
      return r.data.reused ? "Existing owner assigned" : "Owner added";
    }
    // new
    if (!form.email.trim() && !form.name.trim()) return null; // left blank → skip
    const r = await api.post(`/super/restaurants/${rid}/owner`, form);
    return r.data.reused ? "Existing owner assigned" : r.data.invited ? "Owner invited — set-password link emailed" : "Owner created";
  };

  const resetCreate = () => {
    setRForm({ name: "", currency: "INR" });
    setOForm({ name: "", email: "", password: "" });
    setMode("new"); setPickEmail("");
  };

  const createRestaurant = async () => {
    if (!rForm.name.trim()) return toast.error("Restaurant name is required");
    setBusy(true);
    try {
      const res = await api.post("/super/restaurants", rForm);
      const rid = res.data.id;
      let ownerMsg = null;
      try {
        ownerMsg = await attachOwner(rid, mode, oForm, pickEmail);
      } catch (e) {
        // Restaurant exists now; surface the owner problem but don't lose it.
        toast.warning(`Restaurant created, but owner not added: ${e.response?.data?.detail || "error"}`);
        setNewOpen(false); resetCreate(); load(); loadOwners(); setBusy(false); return;
      }
      toast.success(ownerMsg ? `Restaurant created · ${ownerMsg}` : "Restaurant created");
      setNewOpen(false); resetCreate(); load(); loadOwners();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const addOwner = async () => {
    setBusy(true);
    try {
      const msg = await attachOwner(ownerFor.id, mode, oForm, pickEmail);
      if (!msg) { toast.error(mode === "existing" ? "Select an owner" : "Enter owner details"); setBusy(false); return; }
      toast.success(msg);
      setOwnerFor(null); setOForm({ name: "", email: "", password: "" }); setMode("new"); setPickEmail("");
      load(); loadOwners();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const openConsole = (rid) => { localStorage.setItem("qrdine_active_restaurant", rid); nav("/admin/dashboard"); };

  const toggleRestaurant = async (r) => {
    const disabling = r.is_active !== false;
    try {
      await api.patch(`/super/restaurants/${r.id}`, { is_active: !disabling ? true : false });
      toast.success(disabling ? `${r.name} disabled — customers can't order` : `${r.name} enabled`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resendOwnerInvite = async (o) => {
    try {
      await api.post(`/super/owners/${o.id}/resend-invite`);
      toast.success(`Fresh invite emailed to ${o.email} (valid 24h)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteRestaurant = async () => {
    if (!delFor || delName.trim() !== delFor.name) return toast.error("Type the restaurant name exactly to confirm");
    setBusy(true);
    try {
      await api.delete(`/super/restaurants/${delFor.id}`);
      toast.success(`${delFor.name} deleted permanently`);
      setDelFor(null); setDelName(""); load(); loadOwners();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen qd-admin">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[var(--qd-charcoal)] grid place-items-center"><Store className="w-4 h-4 text-white" aria-hidden /></div>
          <div>
            <div className="font-display font-bold text-sm">QRDine Platform</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-400">Super admin · {auth.user?.name || auth.user?.email}</div>
          </div>
        </div>
        <button data-testid="super-logout" onClick={() => { setAdminAuth(null); nav("/admin/login"); }} className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"><LogOut className="w-4 h-4" aria-hidden /> Sign out</button>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-400">Platform</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Restaurants</h1>
          </div>
          <Button data-testid="super-new-restaurant" onClick={() => { resetCreate(); setNewOpen(true); }} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black text-white"><Plus className="w-4 h-4 mr-1" aria-hidden /> New restaurant</Button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {restaurants.map((r) => (
            <div key={r.id} data-testid={`super-restaurant-${r.id}`} className={`bg-white rounded-xl border p-5 ${r.is_active === false ? "border-red-200 opacity-75" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg truncate">
                    {r.name}
                    {r.is_active === false && <span data-testid={`super-disabled-${r.id}`} className="ml-2 align-middle text-[9px] font-bold uppercase tracking-wider bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded">Disabled</span>}
                  </div>
                  <div className="text-xs text-gray-400 font-mono-alt">{r.id} · {r.currency}</div>
                </div>
                <button data-testid={`super-open-${r.id}`} onClick={() => openConsole(r.id)} className="text-xs font-semibold text-[var(--qd-primary)] inline-flex items-center gap-1 shrink-0">Open console <ArrowRight className="w-3.5 h-3.5" aria-hidden /></button>
              </div>
              <div className="flex gap-4 text-xs text-gray-500 mt-3">
                <span>{r.order_count} orders</span><span>{r.menu_count} items</span><span>{r.table_count} tables</span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1 inline-flex items-center gap-1"><Users className="w-3 h-3" aria-hidden /> Owners</div>
                {(r.owners || []).length
                  ? r.owners.map((o) => (
                      <div key={o.id} className="text-xs text-gray-700 flex items-center gap-2">
                        <span className="truncate">{o.name} · {o.email}</span>
                        {o.pending && (
                          <>
                            <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded">Invite pending</span>
                            <button data-testid={`super-resend-${o.id}`} onClick={() => resendOwnerInvite(o)} className="shrink-0 text-[var(--qd-primary)] font-semibold">Resend</button>
                          </>
                        )}
                      </div>
                    ))
                  : <div className="text-xs text-amber-600 font-medium" data-testid={`super-noowner-${r.id}`}>No owner yet</div>}
                <button data-testid={`super-add-owner-${r.id}`} onClick={() => { setOForm({ name: "", email: "", password: "" }); setMode("new"); setPickEmail(""); setOwnerFor(r); }} className="text-xs text-[var(--qd-primary)] font-semibold mt-2">+ Add owner</button>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <button data-testid={`super-toggle-${r.id}`} onClick={() => toggleRestaurant(r)}
                  className={`text-xs font-semibold ${r.is_active === false ? "text-green-700" : "text-amber-600"}`}
                  title={r.is_active === false ? "Customers will be able to order again" : "QRs stop working; owner console stays accessible"}>
                  {r.is_active === false ? "Enable restaurant" : "Disable restaurant"}
                </button>
                <button data-testid={`super-delete-${r.id}`} onClick={() => { setDelName(""); setDelFor(r); }} className="text-xs font-semibold text-red-600 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden /> Delete
                </button>
              </div>
            </div>
          ))}
          {restaurants.length === 0 && <div className="col-span-full text-sm text-gray-400 py-10 text-center">No restaurants yet — create your first.</div>}
        </div>
      </div>

      {/* New restaurant — with inline owner assignment */}
      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) resetCreate(); }}>
        <DialogContent><DialogHeader><DialogTitle>New restaurant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="super-r-name" placeholder="Restaurant name" value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} />
            <Input data-testid="super-r-currency" placeholder="Currency (e.g. INR)" value={rForm.currency} onChange={(e) => setRForm({ ...rForm, currency: e.target.value.toUpperCase() })} />
            <OwnerFields mode={mode} setMode={setMode} oForm={oForm} setOForm={setOForm} pickEmail={pickEmail} setPickEmail={setPickEmail} owners={owners} allowSkip testPrefix="super-r" />
            <Button data-testid="super-r-save" onClick={createRestaurant} disabled={busy} className="w-full bg-[var(--qd-charcoal)] hover:bg-black text-white">{busy ? "Creating…" : "Create restaurant"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete restaurant — type-to-confirm; cascades every scoped record */}
      <Dialog open={!!delFor} onOpenChange={(o) => { if (!o) { setDelFor(null); setDelName(""); } }}>
        <DialogContent><DialogHeader><DialogTitle>Delete {delFor?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <span className="font-semibold">Permanent.</span> Deletes {delFor?.order_count ?? 0} orders, {delFor?.menu_count ?? 0} menu items, {delFor?.table_count ?? 0} tables, plus coupons, reviews, waiter calls, settings and this restaurant's manager accounts. Owner logins survive (they just lose this restaurant). If you only want to pause it, use <span className="font-semibold">Disable</span> instead.
            </div>
            <Input data-testid="super-del-confirm" placeholder={`Type "${delFor?.name}" to confirm`} value={delName} onChange={(e) => setDelName(e.target.value)} />
            <Button data-testid="super-del-go" onClick={deleteRestaurant} disabled={busy || delName.trim() !== (delFor?.name || "")} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40">
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add owner to an existing restaurant */}
      <Dialog open={!!ownerFor} onOpenChange={(o) => { if (!o) { setOwnerFor(null); setMode("new"); setPickEmail(""); } }}>
        <DialogContent><DialogHeader><DialogTitle>Add owner{ownerFor ? ` — ${ownerFor.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <OwnerFields mode={mode} setMode={setMode} oForm={oForm} setOForm={setOForm} pickEmail={pickEmail} setPickEmail={setPickEmail} owners={owners} allowSkip={false} testPrefix="super-add" />
            <Button data-testid="super-o-save" onClick={addOwner} disabled={busy} className="w-full bg-[var(--qd-charcoal)] hover:bg-black text-white">{busy ? "Saving…" : "Add owner"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
